import { AspectRatio, Box, Flex, Heading, SimpleGrid, Text } from "@highoutput/ui-components";
import Image from "next/image";
import type { MemberCoreDataFragment } from "~/__generated__/graphql";
import Footer from "../landing/v2/footer";
import Navbar from "../navbar";
import For from "../utilities/for";
import Card from "./card";

type TeamProps = {
  data: {
    members: MemberCoreDataFragment[];
    highlight?: MemberCoreDataFragment | null;
  };
};

export default function Team({ data }: TeamProps) {
  return (
    <Box>
      <Navbar />

      <Box
        as="main"
        paddingBottom={{
          base: "100px",
          lg: "128px",
        }}
        fontFamily="Inter"
      >
        <Box
          maxWidth={{
            base: "full" /* cover md breakpoint 🚀. change to 375px when necessary 😏 */,
            lg: "1024px",
            xl: "960px",
          }}
          marginX="auto"
          paddingX={{
            base: "16px",
            lg: "24px",
            xl: "unset",
          }}
        >
          <Flex
            paddingTop={{
              base: "64px",
              xl: "80px",
            }}
            paddingBottom={{
              base: "64px",
              lg: "24px",
              xl: "80px",
            }}
            align={"center"}
            direction="column"
          >
            <Heading
              as="h1"
              variant={{
                base: "32/36",
                lg: "56/64",
              }}
              letterSpacing={{
                base: "-0.02em",
                lg: "unset",
              }}
              textAlign="center"
              fontWeight={400}
              color="rgb(13,48,80)"
              w={{ base: "75%" }}
            >
              Meet the Advante Performance Coach Team
            </Heading>

            <Text
              variant={{
                base: "16/24",
                lg: "20/32",
              }}
              marginTop={{
                base: "16px",
                lg: "24px",
              }}
              maxWidth={{
                base: "330px",
                sm: "500px",
                md: "unset",
                xl: "960px",
              }}
              marginX="auto"
              color="rgba(13,48,80,0.8)"
              textAlign="center"
              fontFamily="Inter"
              letterSpacing={{ base: "0.02em", lg: 0 }}
            >
              Advante believes in helping individuals discover their full potential. In order to
              have a productive team that delivers exceptional output a key component is always
              employee empowerment and support - an area often overlooked. Each member of the
              Advante team is eager to find areas of improvement in their coachees and is able to
              understand each team structure in order to provide maximum support.
            </Text>
          </Flex>

          {data.highlight && (
            <Box>
              <Card
                width="unset"
                data={data.highlight}
                renderImage={(member) => (
                  <AspectRatio
                    ratio={{
                      base: 6 / 5,
                      md: 16 / 9,
                    }}
                    rounded="8px"
                    overflow="hidden"
                  >
                    <Image
                      alt={member.name}
                      src={member.photo.data?.attributes?.url ?? ""}
                      width={member.photo.data?.attributes?.width ?? 1000}
                      height={member.photo.data?.attributes?.height ?? 600}
                      priority
                      style={{
                        objectFit: "cover",
                        objectPosition: "center",
                      }}
                    />
                  </AspectRatio>
                )}
              />
            </Box>
          )}

          <SimpleGrid
            columns={{
              base: 1,
              md: 2,
              lg: 3,
            }}
            marginTop={{
              base: "80px",
              lg: "64px",
              xl: "44px",
            }}
            columnGap={{
              md: "32px",
              lg: "32px",
            }}
            rowGap={{
              base: "80px",
              lg: "72px",
            }}
            flexWrap="wrap"
          >
            <For each={data.members}>{(member) => <Card data={member} />}</For>
          </SimpleGrid>
        </Box>
      </Box>

      <Footer cta />
    </Box>
  );
}
